import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["item", "count", "countLabel", "section", "lookupText", "panel", "overlay", "toggleButton", "toggleIcon"]

  connect() {
    this.onLookupChange = this.onLookupChange.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
    this.onDynamicRowAdded = this.onDynamicRowAdded.bind(this)
    this.onRowRemoved = this.onRowRemoved.bind(this)
    document.addEventListener("lookup-selector:change", this.onLookupChange)
    document.addEventListener("default-memory:dynamic-row-added", this.onDynamicRowAdded)
    document.addEventListener("default-memory:row-removed", this.onRowRemoved)
    document.addEventListener("keydown", this.onKeydown)
    this.sync()
  }

  disconnect() {
    document.removeEventListener("lookup-selector:change", this.onLookupChange)
    document.removeEventListener("default-memory:dynamic-row-added", this.onDynamicRowAdded)
    document.removeEventListener("default-memory:row-removed", this.onRowRemoved)
    document.removeEventListener("keydown", this.onKeydown)
    document.body.classList.remove("overflow-hidden")
  }

  toggle() {
    if (this.isOpen()) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (!this.hasPanelTarget) return

    this.panelTarget.classList.remove("translate-x-full")
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("opacity-0", "pointer-events-none")
    }
    document.body.classList.add("overflow-hidden")
    this.setExpanded(true)
  }

  close() {
    if (!this.hasPanelTarget) return

    this.panelTarget.classList.add("translate-x-full")
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("opacity-0", "pointer-events-none")
    }
    document.body.classList.remove("overflow-hidden")
    this.setExpanded(false)
  }

  clickOutside(event) {
    if (this.hasOverlayTarget && event.target === this.overlayTarget) {
      this.close()
    }
  }

  onLookupChange(event) {
    const { memoryKey, lookupId, displayText, touched } = event.detail || {}
    if (!memoryKey) return

    this.itemTargets
      .filter((item) => item.dataset.memoryKey === memoryKey)
      .forEach((item) => this.updateItem(item, lookupId, displayText, touched))

    this.sync()
  }

  onDynamicRowAdded(event) {
    const { fromIndex, toIndex, association } = event.detail || {}
    if (!fromIndex || toIndex === undefined || !association) return

    const token = `[${association}][${fromIndex}]`
    const existingToken = `[${association}][${toIndex}]`
    const prototypes = this.itemTargets.filter((item) => item.dataset.memoryKey?.includes(token))

    prototypes.forEach((prototype) => {
      const nextMemoryKey = prototype.dataset.memoryKey.replace(token, existingToken)
      if (this.itemTargets.some((item) => item.dataset.memoryKey === nextMemoryKey)) return

      const clone = prototype.cloneNode(true)
      this.replaceNestedIndex(clone, association, fromIndex, toIndex)
      clone.classList.add("hidden")

      const checkbox = clone.querySelector('input[type="checkbox"]')
      if (checkbox) {
        checkbox.checked = false
        checkbox.disabled = true
      }
      const touchedInput = clone.querySelector("[data-default-memory-touched-input]")
      if (touchedInput) touchedInput.value = "0"

      prototype.parentElement.insertBefore(clone, prototype.nextSibling)
    })

    this.sync()
  }

  onRowRemoved(event) {
    const memoryKeys = event.detail?.memoryKeys || []
    if (memoryKeys.length === 0) return

    this.itemTargets
      .filter((item) => memoryKeys.includes(item.dataset.memoryKey))
      .forEach((item) => this.hideItem(item))

    this.sync()
  }

  replaceNestedIndex(element, association, fromIndex, toIndex) {
    const from = `[${association}][${fromIndex}]`
    const to = `[${association}][${toIndex}]`

    if (element.dataset.memoryKey) {
      element.dataset.memoryKey = element.dataset.memoryKey.replace(from, to)
    }

    element.querySelectorAll("[name], [id], label[for]").forEach((node) => {
      if (node.name) node.name = node.name.replace(from, to)
      if (node.id) node.id = this.replaceIndexToken(node.id, fromIndex, toIndex)
      if (node.htmlFor) node.htmlFor = this.replaceIndexToken(node.htmlFor, fromIndex, toIndex)
    })
  }

  replaceIndexToken(value, fromIndex, toIndex) {
    return value
      .replaceAll(String(fromIndex), String(toIndex))
      .replaceAll(String(fromIndex).toLowerCase(), String(toIndex))
      .replaceAll(String(fromIndex).toLowerCase().replaceAll("_", "-"), String(toIndex))
  }

  onKeydown(event) {
    if (event.key === "Escape" && this.isOpen()) {
      this.close()
    }
  }

  updateItem(item, lookupId, displayText, touched) {
    const defaultLookupId = item.dataset.defaultLookupId
    const visible = Boolean(lookupId) && Boolean(touched) && lookupId !== defaultLookupId
    const checkbox = item.querySelector('input[type="checkbox"]')
    const touchedInput = item.querySelector("[data-default-memory-touched-input]")
    const lookupText = item.querySelector('[data-default-memory-tray-target="lookupText"]')

    if (lookupText) {
      lookupText.textContent = displayText || ""
    }

    if (!visible) {
      this.hideItem(item)
      return
    }

    item.classList.remove("hidden")

    if (checkbox) {
      checkbox.disabled = false
      checkbox.checked = item.dataset.defaultCheckedOnTouch === "true"
    }

    if (touchedInput) {
      touchedInput.value = "1"
    }
  }

  hideItem(item) {
    item.classList.add("hidden")

    const checkbox = item.querySelector('input[type="checkbox"]')
    if (checkbox) {
      checkbox.checked = false
      checkbox.disabled = true
    }

    const touchedInput = item.querySelector("[data-default-memory-touched-input]")
    if (touchedInput) touchedInput.value = "0"
  }

  isOpen() {
    return this.hasPanelTarget && !this.panelTarget.classList.contains("translate-x-full")
  }

  setExpanded(expanded) {
    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.setAttribute("aria-expanded", expanded ? "true" : "false")
    }

    if (this.hasToggleIconTarget) {
      this.toggleIconTarget.classList.toggle("rotate-180", expanded)
    }
  }

  sync() {
    const visibleItems = this.itemTargets.filter((item) => !item.classList.contains("hidden"))
    this.element.classList.toggle("hidden", visibleItems.length === 0)
    if (visibleItems.length === 0) this.close()

    this.countTargets.forEach((target) => {
      target.textContent = String(visibleItems.length)
    })

    this.countLabelTargets.forEach((target) => {
      target.textContent = this.labelForCount(visibleItems.length)
    })

    this.sectionTargets.forEach((section) => {
      const hasVisibleItem = Array.from(section.querySelectorAll("[data-default-memory-tray-target='item']"))
        .some((item) => !item.classList.contains("hidden"))
      section.classList.toggle("hidden", !hasVisibleItem)
    })
  }

  labelForCount(count) {
    if (count === 1) return "pasiūlymas"
    return "pasiūlymai"
  }
}
